import { IBaseReducer } from '@network/models/base-reducer.model'
import { IBaseGetParams } from '@network/models/global.model'
import { IMyUpshifter } from '@network/models/my-upshifter.model'
import { IUser } from '@network/models/user.model'

export interface IRostersReducer extends IBaseReducer<IRoster[]> {}

export interface IRoster {
  id: number
  status: ROSTER_STATUS
  name: string
  business_id: number
  created_by?: IUser
  members?: {
    data: {
      avatar_url: string
      avatar_thumb_url: string
    }[]
  }
  members_count?: number
  is_available_for_invite?: boolean
}

export enum ROSTER_STATUS {
  ARCHIVED = 0,
  ACTIVE = 1,
  DISABLED = 2,
  DELETED = 3,

  /**
   * Rosters that cannot be edited or have members managed by a user
   */
  LOCKED = 4,
}

export interface IGetRostersThunkParams extends IGetRostersParams {}

export interface IGetRostersParams
  extends IRosterIncludeParams,
    IBaseGetParams {
  has_members?: boolean
  is_available_for_invite_for_gig_id?: number
}

export interface IRosterIncludeParams {
  include?:
    | {
        members?: Record<any, any>
        created_by?: Record<any, any>
        members_count?: Record<any, any>
      }
    | ('members' | 'created_by' | 'members_count')[]
}

export interface IGetRosterParams extends IRosterIncludeParams {}

export interface IPutRosterParams {
  name?: string
}

export interface ICreateRosterParams {
  name: string
  business_id: number
  member_ids?: number[]
}

export enum ROSTER_INVITE_STATUS {
  PENDING = 0,
  FINISHED = 1,
}

/**
 * @reference https://upshift.atlassian.net/browse/UTT-2710
 */
export enum ROSTER_INVITE_TERMINATION_REASON {
  EXCEPTION_THROWN = 0,
  OPEN_POSITIONS_FILLED = 1,
  NO_REMAINING_ROSTER_MEMBERS = 2,
  GIG_STARTS_IN_LESS_THAN_30_MINUTES = 3,
  SHIFT_FREEZE_REACHED = 4,
  GIG_STATUS_IS_DRAFT = 5,
  MAX_BATCH_INVITES_PER_GIG_REACHED = 6,
  GIG_IS_FINISHED = 7,
  ROSTER_FEATURE_DISABLED_FOR_BUSINESS = 8,
  EMPTY_ROSTERS_ARRAY = 9,
}

export interface IRosterRostersInvitesHistoryIncludeParams {
  include?:
    | {
        rosters?: Record<any, any> // what rosters were we inviting from
        causer_user?: Record<any, any> // who created/updated the roster batch invite
        users?: Record<any, any> // what users were invited (returns MyUpshifter object, NOT User object)
      }
    | ('rosters' | 'causer_user' | 'users')[]
}

export interface IGetRostersInvitesHistoryParams
  extends IRosterRostersInvitesHistoryIncludeParams,
    IBaseGetParams {
  gig_id: number
  status?: ROSTER_INVITE_STATUS
  terminated?: boolean // Filter only batch invites that have been terminated - or only those that have not been terminated
  causer_user_id?: number
  termination_reason?: ROSTER_INVITE_TERMINATION_REASON
}

export interface IRosterInviteHistory {
  id: number
  scheduled_at: string
  termination_reason: ROSTER_INVITE_TERMINATION_REASON | null
  status: ROSTER_INVITE_STATUS
  users?: {
    data: IMyUpshifter[]
  }
  rosters?: {
    data: IRoster[]
  }
  causer_user?: IUser
}

export interface IGetRostersInvitesHistoryThunkParams
  extends IGetRostersInvitesHistoryParams {}
